/**
 * Cover image URL helpers.
 *
 * Rewrites Supabase Storage signed URLs (`/storage/v1/object/sign/...`) to the
 * on-the-fly image transformer (`/storage/v1/render/image/sign/...`) so covers
 * are served as WebP/AVIF at the size actually displayed. The stored signed
 * token authorises both endpoints.
 */

const RENDER_WIDTHS = [400, 640, 960, 1280, 1600] as const;
const DEFAULT_QUALITY = 72;

function isSupabaseSignedObjectUrl(url: string): boolean {
  return url.includes("/storage/v1/object/sign/");
}

/**
 * Return a transformed variant of a Supabase signed cover URL.
 * Non-Supabase / non-signed URLs are returned unchanged so external images and
 * placeholders still work.
 */
export function transformCoverUrl(
  url: string | null | undefined,
  opts: { width: number; height?: number; quality?: number } = { width: 1280 },
): string | null {
  if (!url) return null;
  if (!isSupabaseSignedObjectUrl(url)) return url;
  const [base, token] = url.split("?token=");
  if (!token) return url;
  const rendered = base.replace("/object/sign/", "/render/image/sign/");
  const params = new URLSearchParams({
    token,
    width: String(opts.width),
    quality: String(opts.quality ?? DEFAULT_QUALITY),
    resize: "cover",
  });
  if (opts.height) params.set("height", String(opts.height));
  return `${rendered}?${params.toString()}`;
}

export interface CoverSources {
  src: string;
  srcSet: string;
  sizes: string;
}

/**
 * Build a responsive srcset for a cover image.
 * Widths cover the largest display size (article hero at ~1600 CSS px on 2x DPR)
 * down to mobile card thumbnails.
 */
export function coverSrcSet(
  url: string | null | undefined,
  opts: { sizes: string; largest?: number } = { sizes: "100vw" },
): CoverSources | null {
  if (!url) return null;
  if (!isSupabaseSignedObjectUrl(url)) {
    return { src: url, srcSet: url, sizes: opts.sizes };
  }
  const largest = opts.largest ?? 1280;
  const widths = RENDER_WIDTHS.filter((w) => w <= largest * 1.25);
  const src = transformCoverUrl(url, { width: largest })!;
  const srcSet = widths
    .map((w) => `${transformCoverUrl(url, { width: w })} ${w}w`)
    .join(", ");
  return { src, srcSet, sizes: opts.sizes };
}

/** Sizes attributes for each cover context. */
export const COVER_SIZES = {
  feature: "(min-width: 768px) 640px, 100vw",
  card: "(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw",
  row: "(min-width: 640px) 176px, 100vw",
  hero: "(min-width: 1280px) 1200px, 100vw",
} as const;
